import React, { useState } from "react";
import {
    Card,
    CardContent,
    CardMedia,
    Typography,
    Grid,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
} from "@mui/material";
import { Phone } from "@mui/icons-material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { useNavigate } from "react-router-dom";

const HotelCard = ({ hotel, customization = {} }) => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    if (!hotel) return null;

    const renderStars = (rating) =>
        Array.from({ length: 5 }, (_, i) =>
            i < rating ? (
                <StarIcon key={i} sx={{ color: "#FFD700" }} />
            ) : (
                <StarBorderIcon key={i} sx={{ color: "#FFD700" }} />
            )
        );

    const mapSrc = hotel.Map
        ? `https://maps.google.com/maps?q=${hotel.Map.replace("|", ",")}&output=embed`
        : null;

    const facilities = hotel.HotelFacilities || [];
    const displayedFacilities = facilities.slice(0, 3);
    const remainingCount = facilities.length - displayedFacilities.length;

    const extractDescription = (description) => {
        if (!description) return "";

        const headlineMatch = description.match(/HeadLine\s*:\s*(.*?)<\/p>/i);
        const locationMatch = description.match(/Location\s*:\s*(.*?)<\/p>/i);
        const specialInstructionsMatch = description.match(/Special Instructions\s*:\s*(.*?)<\/p>/i);
        const disclaimerMatch = description.match(/Disclaimer notification:\s*(.*?)<\/b>/i);

        return {
            headline: headlineMatch ? headlineMatch[1] : "",
            location: locationMatch ? locationMatch[1] : "",
            specialInstructions: specialInstructionsMatch ? specialInstructionsMatch[1] : "",
            disclaimer: disclaimerMatch ? disclaimerMatch[1] : "",
        };
    };

    const descriptionParts = extractDescription(hotel.Description);
    const handleClick = () => {
        navigate("/bookinghotel", { state: { hotel } }); // Pass hotel data directly
    };
    return (
        <Card
            onClick={handleClick}
            sx={{
                display: "flex",
                flexDirection: "row",
                width: "100%",
                height: "100%",
                borderRadius: 4,
                boxShadow: customization.boxShadow || 5,
                margin: customization.cardMargin || "20px auto",
                padding: customization.cardPadding || 2,
                background: customization.background || "linear-gradient(to bottom, #ffffff, #f8f8f8)",
                transition: "transform 0.3s ease",
                "&:hover": {
                    transform: customization.hoverTransform || "scale(1.03)",
                },
                ...customization.cardStyle,
            }}
        >
            {/* Hotel Image */}
            {hotel.ImageUrl && (
                <CardMedia
                    component="img"
                    sx={{
                        width: customization.imageWidth || 220,
                        height: customization.imageHeight || "auto",
                        aspectRatio: customization.imageAspectRatio || "unset",
                        objectFit: "cover",
                        borderRadius: "8px",
                        marginRight: "16px",
                        ...customization.imageStyle,
                    }}
                    image={hotel.ImageUrl || "https://via.placeholder.com/220x220?text=Hotel+Image"}
                    alt={hotel.HotelName}
                />
            )}

            <CardContent
                sx={{
                    flex: "1",
                    padding: customization.contentPadding || "20px",
                    ...customization.contentStyle,
                }}
            >
                {/* Hotel Name */}
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontFamily: customization.titleFontFamily, fontSize: customization.titleFontSize, color: customization.titleColor }}>
                    {hotel.HotelName}
                </Typography>

                {/* Address */}
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    {hotel.Address}
                </Typography>

                {/* Star Rating */}
                <Grid container alignItems="center" spacing={1} mt={1}>
                    <Grid item>{renderStars(hotel.HotelRating)}</Grid>
                </Grid>

                {/* Headline */}
                {descriptionParts.headline && (
                    <Typography variant="body1" fontWeight="bold" mt={2}>
                        {descriptionParts.headline}
                    </Typography>
                )}

                {/* Location */}
                {descriptionParts.location && (
                    <Typography variant="body2" color="text.secondary" mt={1}>
                        {descriptionParts.location}
                    </Typography>
                )}

                {/* Special Instructions */}
                {descriptionParts.specialInstructions && (
                    <Typography variant="body2" mt={1} fontStyle="italic">
                        {descriptionParts.specialInstructions}
                    </Typography>
                )}

                {/* Disclaimer */}
                {descriptionParts.disclaimer && (
                    <Typography variant="body2" mt={1} color="error">
                        {descriptionParts.disclaimer}
                    </Typography>
                )}

                {/* Facilities */}
                <Grid container spacing={1} mt={2}>
                    {displayedFacilities.map((facility, index) => (
                        <Grid item key={index}>
                            <Chip
                                label={facility}
                                variant="outlined"
                                sx={{
                                    backgroundColor: customization.chipBackgroundColor || "#e3f2fd",
                                    color: customization.chipTextColor || "inherit",
                                    fontWeight: "bold",
                                    padding: "5px",
                                    ...customization.chipStyle,
                                }}
                            />
                        </Grid>
                    ))}
                    {remainingCount > 0 && (
                        <Grid item>
                            <Chip
                                label={`+${remainingCount} more`}
                                variant="outlined"
                                onClick={() => setOpen(true)}
                                sx={{
                                    cursor: "pointer",
                                    backgroundColor: customization.moreChipBackgroundColor || "#d1c4e9",
                                    color: customization.moreChipTextColor || "#4527a0",
                                    fontWeight: "bold",
                                    padding: "5px",
                                    ...customization.moreChipStyle,
                                }}
                            />
                        </Grid>
                    )}
                </Grid>

                {/* Contact Button */}
                <Grid container spacing={2} mt={2} alignItems="center">
                    {hotel.PhoneNumber && (
                        <Grid item>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<Phone />}
                                href={`tel:${hotel.PhoneNumber}`}
                                size="small"
                                sx={{
                                    padding: "8px 16px",
                                    fontWeight: "bold",
                                    background: customization.buttonBackground || "#388e3c",
                                    "&:hover": {
                                        background: customization.buttonHoverBackground || "#2e7d32",
                                    },
                                    ...customization.buttonStyle,
                                }}
                            >
                                Call Hotel
                            </Button>
                        </Grid>
                    )}
                </Grid>
            </CardContent>

            {/* Hotel Map */}
            {mapSrc && (
                <Box
                    sx={{
                        width: customization.mapWidth || 200,
                        height: customization.mapHeight || "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "10px",
                        marginLeft: "16px",
                        ...customization.mapContainerStyle,
                    }}
                >
                    <iframe
                        src={mapSrc}
                        width="100%"
                        height="160"
                        style={{
                            border: "2px solid #ddd",
                            borderRadius: 10,
                            boxShadow: "3px 3px 6px rgba(0,0,0,0.2)",
                            ...customization.iframeStyle,
                        }}
                        allowFullScreen
                        loading="lazy"
                    ></iframe>
                </Box>
            )}

            {/* Dialog for all facilities */}
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>All Facilities</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexWrap="wrap" gap={1} p={2}>
                        {facilities.map((facility, index) => (
                            <Chip
                                key={index}
                                label={facility}
                                variant="outlined"
                                sx={{
                                    backgroundColor: customization.dialogChipBackgroundColor || "#f5f5f5",
                                    color: customization.dialogChipTextColor || "inherit",
                                    fontWeight: "bold",
                                    padding: "6px",
                                    ...customization.dialogChipStyle,
                                }}
                            />
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOpen(false)}
                        color="primary"
                        sx={{
                            fontWeight: "bold",
                            ...customization.dialogButtonStyle,
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>

    );
};

export default HotelCard;
